import type { Game, GameState } from '../game/Game';
import { MAX_STEPS, multiplierForStep } from '@benji/sim-core';

export class UIOverlay {
  private root: HTMLElement;
  private game: Game;

  private balanceEl: HTMLElement;
  private betInput: HTMLInputElement;
  private betDisplayEl: HTMLElement;
  private betMinusBtn: HTMLButtonElement;
  private betPlusBtn: HTMLButtonElement;
  private multiplierEl: HTMLElement;
  private multiplierNumEl: HTMLElement;
  private multiplierSubEl: HTMLElement;
  private stepEl: HTMLElement;
  private ladderSlots: HTMLElement[];
  private primaryActionEl: HTMLButtonElement;
  private secondaryActionEl: HTMLButtonElement;
  private primarySubEl: HTMLElement;
  private secondarySubEl: HTMLElement;

  constructor(root: HTMLElement, game: Game) {
    this.root = root;
    this.game = game;

    this.balanceEl = this.q('[data-balance]');
    this.betInput = this.q<HTMLInputElement>('[data-bet]');
    this.betDisplayEl = this.q('[data-bet-display]');
    this.betMinusBtn = this.q<HTMLButtonElement>('[data-bet-minus]');
    this.betPlusBtn = this.q<HTMLButtonElement>('[data-bet-plus]');
    this.multiplierEl = this.q('[data-mult]');
    this.multiplierNumEl = this.q('[data-mult-num]');
    this.multiplierSubEl = this.q('[data-mult-sub]');
    this.stepEl = this.q('[data-step]');
    this.ladderSlots = Array.from(this.root.querySelectorAll<HTMLElement>('[data-ladder-slot]'));
    this.primaryActionEl = this.q<HTMLButtonElement>('[data-action-slot="primary"]');
    this.secondaryActionEl = this.q<HTMLButtonElement>('[data-action-slot="secondary"]');
    this.primarySubEl = this.primaryActionEl.querySelector('[data-action-sub]') as HTMLElement;
    this.secondarySubEl = this.secondaryActionEl.querySelector('[data-action-sub]') as HTMLElement;

    this.bindHandlers();
  }

  private q<T extends HTMLElement = HTMLElement>(selector: string): T {
    return this.root.querySelector(selector) as T;
  }

  private bindHandlers() {
    const handle = (el: HTMLButtonElement) => {
      if (el.disabled) return;
      const intent = el.dataset.intent;
      if (intent === 'jump') this.game.jump();
      else if (intent === 'cashout') this.game.cashOut();
      else if (intent === 'tryagain') this.game.tryAgain();
    };
    this.primaryActionEl.addEventListener('click', () => handle(this.primaryActionEl));
    this.secondaryActionEl.addEventListener('click', () => handle(this.secondaryActionEl));

    this.betMinusBtn.addEventListener('click', () => {
      if (!this.betMinusBtn.disabled) this.game.cycleBet(-1);
    });
    this.betPlusBtn.addEventListener('click', () => {
      if (!this.betPlusBtn.disabled) this.game.cycleBet(1);
    });
  }

  setBalance(b: number) {
    this.balanceEl.textContent = '$' + b.toFixed(2).replace(/\.00$/, '');
  }

  setBet(b: number) {
    this.betInput.value = String(b);
    this.betDisplayEl.textContent = '$' + b.toFixed(2);
  }

  refreshRound(state: GameState, step: number, multiplier: number, bet: number) {
    this.multiplierEl.className = 'hud-card ' + state;
    this.multiplierNumEl.textContent = multiplier.toFixed(2);

    const stepLabel = `STEP ${Math.min(step + 1, MAX_STEPS)}/${MAX_STEPS}`;
    this.stepEl.textContent = stepLabel;

    if (state === 'waiting') {
      const potentialWin = bet * multiplier;
      this.multiplierSubEl.textContent = step === 0 ? 'JUMP TO START EARNING' : `SECURED $${formatMoney(potentialWin)}`;
    } else if (state === 'jumping') {
      this.multiplierSubEl.textContent = 'SWINGING…';
    } else if (state === 'cashed') {
      this.multiplierSubEl.textContent = `CASHED OUT $${formatMoney(bet * multiplier)}`;
    } else if (state === 'crashed') {
      this.multiplierSubEl.textContent = 'VINE SNAPPED!';
    }

    const slots = [step, step + 1, step + 2].filter((s) => s <= MAX_STEPS);
    this.ladderSlots.forEach((el, i) => {
      if (i < slots.length) {
        el.textContent = multiplierForStep(slots[i]).toFixed(2) + 'x';
        el.className = i === 0 ? 'tag tag-mult selected' : 'tag tag-mult';
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });

    this.betMinusBtn.disabled = !this.game.betCanDecrease();
    this.betPlusBtn.disabled = !this.game.betCanIncrease();

    this.renderActions(state, step, multiplier, bet);
  }

  private renderActions(state: GameState, step: number, multiplier: number, bet: number) {
    const win = bet * multiplier;
    const canCash = step >= 1;
    const nextMultLabel = `NEXT ${multiplierForStep(step + 1).toFixed(2)}`;
    const winLabel = canCash ? '$' + formatMoney(win) : '$0';

    if (state === 'waiting') {
      this.setAction(this.primaryActionEl, this.primarySubEl, 'action primary jump', 'jump', false, nextMultLabel);
      this.setAction(this.secondaryActionEl, this.secondarySubEl, 'action secondary cashout', 'cashout', !canCash, winLabel);
    } else if (state === 'jumping') {
      this.setAction(this.primaryActionEl, this.primarySubEl, 'action primary jumping', '', true, nextMultLabel);
      this.setAction(this.secondaryActionEl, this.secondarySubEl, 'action secondary cashout', '', true, winLabel);
    } else if (state === 'cashed') {
      this.setAction(this.primaryActionEl, this.primarySubEl, 'action primary playagain', 'tryagain', false, '');
      this.setAction(this.secondaryActionEl, this.secondarySubEl, 'action primary cashed-state', '', true, '$' + formatMoney(win));
    } else if (state === 'crashed') {
      this.setAction(this.primaryActionEl, this.primarySubEl, 'action primary tryagain', 'tryagain', false, '');
      this.setAction(this.secondaryActionEl, this.secondarySubEl, 'action secondary cashout', '', true, winLabel);
    }
  }

  private setAction(
    button: HTMLButtonElement,
    sub: HTMLElement,
    className: string,
    intent: string,
    disabled: boolean,
    label: string,
  ) {
    button.className = className;
    button.dataset.intent = intent;
    button.disabled = disabled;
    sub.textContent = label;
  }
}

function formatMoney(v: number): string {
  return v.toFixed(2).replace(/\.00$/, '');
}
